// =============================================================================
// AISurfacesWidget — predictive battery signals computed from real readings.
//
// Three signals: highest risk unit, biggest voltage drop, longest-since-check.
// Each card is a real button — clicking navigates to that unit's check-in
// page so the porter can act immediately. No dead clicks, no fake data.
// =============================================================================
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useBatteryUnits, voltageBucket, isDropping, daysSinceCheck, unitName,
} from "../lib/useBatteryData.js";

export function AISurfacesWidget({ config }) {
  const nav = useNavigate();
  const { data: units, loading } = useBatteryUnits();

  const insights = useMemo(() => computeInsights(units, config), [units, config]);

  // Headline tracks reality — don't promise three when only one surfaces.
  const headline = headlineFor(insights.length);

  return (
    <div className="w-shell">
      <header className="w-head">
        <div className="w-eye"><span className="w-eye-dot" />SEVRIC AI · BEFORE YOU WALK THE LOT</div>
        <h3 className="w-h3" dangerouslySetInnerHTML={{ __html: headline }} />
      </header>

      {loading && <div className="w-loading">Reading the floor…</div>}

      {!loading && insights.length === 0 && (
        <div className="w-empty">
          <div className="w-empty-h">All units look healthy.</div>
          <div className="w-empty-s">Once a battery dips, drops, or goes too long without a check, it surfaces here.</div>
        </div>
      )}

      {!loading && insights.length > 0 && (
        <div
          className="w-ai-grid"
          // Columns match the count — 1 card spans full width, 2 split,
          // 3 take thirds. Stops the "empty third column" alignment bug.
          style={{ gridTemplateColumns: `repeat(${Math.min(insights.length, 3)}, minmax(0, 1fr))` }}
        >
          {insights.map((ai) => (
            <button
              key={ai.id}
              type="button"
              className={`w-ai-card${ai.kind === 'watch' ? ' watch' : ai.kind === 'win' ? ' win' : ''}`}
              onClick={() => ai.href && nav(ai.href)}
              style={{ cursor: ai.href ? 'pointer' : 'default' }}
            >
              <div className="w-ai-badge">AI</div>
              <div>
                <div className="w-ai-eye"><span>{ai.eye}</span><span className="w-ai-stamp">{ai.stamp}</span></div>
                <div className="w-ai-title" dangerouslySetInnerHTML={{ __html: ai.title }} />
                <div className="w-ai-detail">{ai.detail}</div>
                {ai.cta && <div className="w-ai-cta">{ai.cta} →</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Headline copy adapts to how many cards actually surfaced. We never claim
// "three things" when only one or two real signals exist — the headline has
// to be true at a glance.
function headlineFor(n) {
  if (n === 1) return "One thing <em>worth seeing</em>.";
  if (n === 2) return "Two things <em>worth seeing</em>.";
  return "Three things <em>worth seeing</em>.";
}

// ============================================================================
// Local insight computation (same shape aiProxy will eventually return).
// ============================================================================
function computeInsights(units) {
  const insights = [];
  const stamp = nowStamp();

  // ---- 1. Lowest-voltage unit (the biggest risk right now) -------------
  const ranked = units
    .map((u) => ({ u, v: Number(u.voltage), bucket: voltageBucket(u) }))
    .filter((x) => x.bucket === "risk" || x.bucket === "warn")
    .sort((a, b) => a.v - b.v);
  if (ranked.length > 0) {
    const { u, v, bucket } = ranked[0];
    insights.push({
      id: `risk-${u.id}`,
      kind: "watch",
      eye: bucket === "risk" ? "WATCH · BELOW 12.0V" : "WATCH · APPROACHING RISK",
      stamp,
      title: `${esc(unitName(u))} — <em>${esc(u.stockNumber || u.id.slice(0,8))}</em> at <em>${v.toFixed(2)}V</em>.`,
      detail: ranked.length > 1
        ? `Plus ${ranked.length - 1} other ${ranked.length - 1 === 1 ? "unit is" : "units are"} below 12.4V. Start with this one.`
        : `Lowest reading on the floor. Get a charger on it before it slides further.`,
      cta: "Check the unit",
      href: `/check-in/${u.id}`,
    });
  }

  // ---- 2. Biggest voltage drop between last two readings ---------------
  const dropping = units
    .map((u) => ({ u, drop: voltageDrop(u) }))
    .filter((x) => x.drop >= 0.3)
    .sort((a, b) => b.drop - a.drop);
  if (dropping.length > 0 && dropping[0].u.id !== ranked[0]?.u.id) {
    const { u, drop } = dropping[0];
    insights.push({
      id: `drop-${u.id}`,
      kind: "watch",
      eye: `WATCH · DROPPED ${drop.toFixed(2)}V`,
      stamp,
      title: `${esc(unitName(u))} dropped <em>${drop.toFixed(2)}V</em> between checks.`,
      detail: `Last reading is still in spec, but the trend is the warning. Walk it over and put it on the charger.`,
      cta: "Check the unit",
      href: `/check-in/${u.id}`,
    });
  }

  // ---- 3. Longest-since-check unit (stale) -----------------------------
  const stale = units
    .map((u) => ({ u, days: daysSinceCheck(u) }))
    .filter((x) => x.days !== null && x.days >= 7)
    .sort((a, b) => b.days - a.days);
  if (stale.length > 0 && !insights.find((i) => i.id.endsWith(stale[0].u.id))) {
    const { u, days } = stale[0];
    insights.push({
      id: `stale-${u.id}`,
      kind: "info",
      eye: `NEXT · ${days} DAYS UNCHECKED`,
      stamp,
      title: `${esc(unitName(u))} hasn't been read in <em>${days} days</em>.`,
      detail: `Pop a meter on it today so we know where it stands before delivery.`,
      cta: "Check the unit",
      href: `/check-in/${u.id}`,
    });
  }

  // ---- Fallback: positive signal if nothing's flagged -----------------
  if (insights.length === 0 && units.length > 0) {
    insights.push({
      id: "all-clear",
      kind: "win",
      eye: "WIN · ON PACE",
      stamp,
      title: `All <em>${units.length} units</em> on the floor are healthy.`,
      detail: "Nothing under 12.4V, nothing dropping, no stale checks. Quietest morning of the week.",
      cta: "Open inventory",
      href: "/inventory",
    });
  }

  return insights.slice(0, 3);
}

function voltageDrop(u) {
  const h = Array.isArray(u.history) ? u.history.filter((e) => Number.isFinite(Number(e.voltage))) : [];
  if (h.length < 2) return 0;
  const latest = Number(h[h.length - 1].voltage);
  const prior  = Number(h[h.length - 2].voltage);
  return Math.max(0, prior - latest);
}

function nowStamp() {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function pad(n) { return n < 10 ? `0${n}` : String(n); }
function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({
    "&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;",
  }[c]));
}
